import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import PayrollMetrics from '../components/payroll/PayrollMetrics';
import PayrollChart from '../components/payroll/PayrollChart';
import PayrollFilters from '../components/payroll/PayrollFilters';
import ContractBulkImportModal from '../components/payroll/ContractBulkImportModal';
import DeductionRulesPage from './DeductionRulesPage';
import staffService from '../services/staffService';
import payrollService from '../services/payrollService';
import {
  Plus, Eye, Download, FilePdf, CircleNotch, DownloadIcon,
  CheckCircle, Clock, Warning, ArrowDown, ArrowUp, Info, CaretDown, X,
  ClipboardText, FloppyDisk, ChartBar, XCircle, Money, Trash
} from '@phosphor-icons/react';
import '../styles/PayrollPage.css';

function formatCurrency(amount) {
  return `GHS ${parseFloat(amount).toLocaleString('en-GH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function PayrollPage() {
  const [view, setView] = useState('contracts'); // contracts, runs, loans, encashments, or rules
  const [contracts, setContracts] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [loans, setLoans] = useState([]);
  const [leaveEncashments, setLeaveEncashments] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runLineItems, setRunLineItems] = useState([]);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipData, setPayslipData] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [annualSummaryYear, setAnnualSummaryYear] = useState(new Date().getFullYear());

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'staff_name', direction: 'asc' });
  const [runSortConfig, setRunSortConfig] = useState({ key: 'period_name', direction: 'desc' });
  
  // Modals
  const [showContractModal, setShowContractModal] = useState(false);
  const [showContractDetailsModal, setShowContractDetailsModal] = useState(false);
  const [showGenerateRunModal, setShowGenerateRunModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [showLineItemsModal, setShowLineItemsModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showEncashmentModal, setShowEncashmentModal] = useState(false);

  // Dialog Modal
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });
  
  // Forms
  const [contractForm, setContractForm] = useState({
    staff_id: '',
    basic_salary: '',
    pay_schedule: 'monthly',
    allowance_housing: '',
    allowance_transport: '',
    allowance_meals: '',
    allowance_utilities: '',
    allowance_other: '',
    allowance_other_description: '',
    tax_rate_percent: '',
    pension_rate_percent: '',
    nssf_rate_percent: '',
    other_deduction: '',
    other_deduction_description: '',
    effective_from: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [runForm, setRunForm] = useState({
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth() + 1,
    pay_schedule: 'monthly',
    notes: ''
  });

  const [staffList, setStaffList] = useState([]);
  const [paying, setPaying] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentForm, setAdjustmentForm] = useState({ staff_id: '', adjustment_type: 'bonus', amount: '', reason: '' });
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);

  const [loanForm, setLoanForm] = useState({
    staff_id: '',
    loan_type: 'loan',
    principal_amount: '',
    installment_amount: '',
    total_installments: '',
    start_period_year: new Date().getFullYear(),
    start_period_month: new Date().getMonth() + 1,
    reason: ''
  });

  const [encashmentForm, setEncashmentForm] = useState({
    staff_id: '',
    leave_days: '',
    reason: ''
  });

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Load contracts
  const loadContracts = useCallback(() => {
    setLoading(true);
    payrollService.listContracts(50, 0, false)
      .then(data => setContracts(data.items || []))
      .catch(err => {
        console.error('Error loading contracts:', err);
        toast.error('Failed to load contracts');
      })
      .finally(() => setLoading(false));
  }, []);

  const loadExpiringContracts = useCallback(() => {
    payrollService.listExpiringContracts(30)
      .then(data => setExpiringContracts(data || []))
      .catch(err => console.error('Error loading expiring contracts:', err));
  }, []);

  const handleRenewContract = (contractId) => {
    payrollService.renewContract(contractId)
      .then(() => {
        toast.success('Contract renewed');
        loadContracts();
        loadExpiringContracts();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to renew contract'));
  };

  // Load payroll runs
  const loadPayrollRuns = useCallback(() => {
    setLoading(true);
    payrollService.listRuns(50, 0)
      .then(data => setPayrollRuns(data.items || []))
      .catch(err => {
        console.error('Error loading payroll runs:', err);
        toast.error('Failed to load payroll runs');
      })
      .finally(() => setLoading(false));
  }, []);

  // Load staff loans/advances
  const loadLoans = useCallback(() => {
    setLoading(true);
    payrollService.listLoans()
      .then(data => setLoans(data.items || []))
      .catch(err => {
        console.error('Error loading staff loans:', err);
        toast.error('Failed to load staff loans');
      })
      .finally(() => setLoading(false));
  }, []);

  // Load leave encashment requests
  const loadEncashments = useCallback(() => {
    setLoading(true);
    payrollService.listLeaveEncashments()
      .then(data => setLeaveEncashments(data.items || []))
      .catch(err => {
        console.error('Error loading leave encashment requests:', err);
        toast.error('Failed to load leave encashment requests');
      })
      .finally(() => setLoading(false));
  }, []);

  // Load staff list for contract creation
  useEffect(() => {
    staffService.listStaff({ limit: 100 })
      .then(data => setStaffList(data.items || []))
      .catch(err => console.error('Error loading staff:', err));
  }, []);

  useEffect(() => {
    if (view === 'contracts') {
      loadContracts();
      loadExpiringContracts();
    } else if (view === 'runs') {
      loadPayrollRuns();
    } else if (view === 'loans') {
      loadLoans();
    } else if (view === 'encashments') {
      loadEncashments();
    }
  }, [view, loadContracts, loadPayrollRuns, loadLoans, loadEncashments]);

  // View contract details
  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setShowContractDetailsModal(true);
  };

  // Create contract
  const handleCreateContract = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...contractForm,
        basic_salary: parseFloat(contractForm.basic_salary),
        allowance_housing: parseFloat(contractForm.allowance_housing) || 0,
        allowance_transport: parseFloat(contractForm.allowance_transport) || 0,
        allowance_meals: parseFloat(contractForm.allowance_meals) || 0,
        allowance_utilities: parseFloat(contractForm.allowance_utilities) || 0,
        allowance_other: parseFloat(contractForm.allowance_other) || 0,
        tax_rate_percent: parseFloat(contractForm.tax_rate_percent) || 0,
        pension_rate_percent: parseFloat(contractForm.pension_rate_percent) || 0,
        nssf_rate_percent: parseFloat(contractForm.nssf_rate_percent) || 0,
        other_deduction: parseFloat(contractForm.other_deduction) || 0,
        effective_from: `${contractForm.effective_from}T00:00:00`
      };

      await payrollService.createContract(payload);
      toast.success('Payroll contract created successfully!');
      setShowContractModal(false);
      setContractForm({
        staff_id: '',
        basic_salary: '',
        pay_schedule: 'monthly',
        allowance_housing: '',
        allowance_transport: '',
        allowance_meals: '',
        allowance_utilities: '',
        allowance_other: '',
        allowance_other_description: '',
        tax_rate_percent: '',
        pension_rate_percent: '',
        nssf_rate_percent: '',
        other_deduction: '',
        other_deduction_description: '',
        effective_from: new Date().toISOString().split('T')[0],
        notes: ''
      });
      loadContracts();
    } catch (error) {
      toast.error(error.message || 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate payroll run
  const handleGenerateRun = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        period_year: parseInt(runForm.period_year),
        period_month: parseInt(runForm.period_month),
        pay_schedule: runForm.pay_schedule,
        notes: runForm.notes
      };

      const result = await payrollService.generateRun(payload);
      toast.success(`Payroll run generated: ${result.message}`);
      setShowGenerateRunModal(false);
      setRunForm({
        period_year: new Date().getFullYear(),
        period_month: new Date().getMonth() + 1,
        pay_schedule: 'monthly',
        notes: ''
      });
      loadPayrollRuns();
    } catch (error) {
      toast.error(error.message || 'Failed to generate payroll run');
    } finally {
      setSubmitting(false);
    }
  };

  // View line items
  const handleViewLineItems = async (runId) => {
    try {
      const [lineData, adjData] = await Promise.all([
        payrollService.getRunLineItems(runId, 100, 0),
        payrollService.listAdjustments(runId).catch(() => ({ items: [] })),
      ]);
      setRunLineItems(lineData.items || []);
      setAdjustments(adjData.items || []);
      setSelectedRun(runId);
      setShowLineItemsModal(true);
    } catch (error) {
      toast.error('Failed to load line items');
    }
  };

  const loadAdjustments = async (runId) => {
    try {
      const data = await payrollService.listAdjustments(runId);
      setAdjustments(data.items || []);
    } catch (error) {
      setAdjustments([]);
    }
  };

  const handleCreateAdjustment = async (e) => {
    e.preventDefault();
    if (!adjustmentForm.staff_id || !adjustmentForm.amount || !adjustmentForm.reason) {
      toast.error('Select a staff member and fill in amount and reason');
      return;
    }
    try {
      setSubmittingAdjustment(true);
      const signedAmount = adjustmentForm.adjustment_type === 'penalty' || adjustmentForm.adjustment_type === 'advance_recovery'
        ? -Math.abs(parseFloat(adjustmentForm.amount))
        : Math.abs(parseFloat(adjustmentForm.amount));
      await payrollService.createAdjustment({
        payroll_run_id: selectedRun,
        staff_id: adjustmentForm.staff_id,
        adjustment_type: adjustmentForm.adjustment_type,
        amount: signedAmount,
        reason: adjustmentForm.reason,
      });
      toast.success('Adjustment created, pending approval');
      setAdjustmentForm({ staff_id: '', adjustment_type: 'bonus', amount: '', reason: '' });
      loadAdjustments(selectedRun);
    } catch (error) {
      toast.error(error.message || 'Unable to create adjustment');
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  const handleApproveAdjustment = async (adjustmentId) => {
    try {
      await payrollService.approveAdjustment(adjustmentId);
      toast.success('Adjustment approved and applied to net pay');
      loadAdjustments(selectedRun);
      handleViewLineItems(selectedRun);
    } catch (error) {
      toast.error(error.message || 'Unable to approve adjustment');
    }
  };

  const handleDeleteAdjustment = async (adjustmentId) => {
    try {
      await payrollService.deleteAdjustment(adjustmentId);
      toast.success('Adjustment removed');
      loadAdjustments(selectedRun);
    } catch (error) {
      toast.error(error.message || 'Unable to delete adjustment');
    }
  };

  // ---- Staff loans / advances ----

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    if (!loanForm.staff_id || !loanForm.principal_amount || !loanForm.installment_amount || !loanForm.total_installments) {
      toast.error('Fill in staff member, principal, installment amount, and number of installments');
      return;
    }
    setSubmitting(true);
    try {
      await payrollService.createLoan({
        staff_id: loanForm.staff_id,
        loan_type: loanForm.loan_type,
        principal_amount: parseFloat(loanForm.principal_amount),
        installment_amount: parseFloat(loanForm.installment_amount),
        total_installments: parseInt(loanForm.total_installments, 10),
        start_period_year: parseInt(loanForm.start_period_year, 10),
        start_period_month: parseInt(loanForm.start_period_month, 10),
        reason: loanForm.reason || undefined
      });
      toast.success('Loan request created, pending approval');
      setShowLoanModal(false);
      setLoanForm({
        staff_id: '', loan_type: 'loan', principal_amount: '', installment_amount: '',
        total_installments: '', start_period_year: new Date().getFullYear(),
        start_period_month: new Date().getMonth() + 1, reason: ''
      });
      loadLoans();
    } catch (error) {
      toast.error(error.message || 'Unable to create loan request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveLoan = async (loanId) => {
    try {
      await payrollService.approveLoan(loanId);
      toast.success('Loan approved and disbursed');
      loadLoans();
    } catch (error) {
      toast.error(error.message || 'Unable to approve loan');
    }
  };

  const handleRejectLoan = async (loanId) => {
    try {
      await payrollService.rejectLoan(loanId);
      toast.success('Loan rejected');
      loadLoans();
    } catch (error) {
      toast.error(error.message || 'Unable to reject loan');
    }
  };

  // ---- Leave encashment ----

  const handleCreateEncashment = async (e) => {
    e.preventDefault();
    if (!encashmentForm.staff_id || !encashmentForm.leave_days) {
      toast.error('Select a staff member and enter the number of leave days');
      return;
    }
    setSubmitting(true);
    try {
      await payrollService.createLeaveEncashment({
        staff_id: encashmentForm.staff_id,
        leave_days: parseFloat(encashmentForm.leave_days),
        reason: encashmentForm.reason || undefined
      });
      toast.success('Leave encashment request created, pending approval');
      setShowEncashmentModal(false);
      setEncashmentForm({ staff_id: '', leave_days: '', reason: '' });
      loadEncashments();
    } catch (error) {
      toast.error(error.message || 'Unable to create leave encashment request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveEncashment = async (requestId) => {
    try {
      await payrollService.approveLeaveEncashment(requestId);
      toast.success('Leave encashment approved — it will be paid on the next payroll run');
      loadEncashments();
    } catch (error) {
      toast.error(error.message || 'Unable to approve leave encashment');
    }
  };

  const handleRejectEncashment = async (requestId) => {
    try {
      await payrollService.rejectLeaveEncashment(requestId);
      toast.success('Leave encashment rejected');
      loadEncashments();
    } catch (error) {
      toast.error(error.message || 'Unable to reject leave encashment');
    }
  };

  // View payslip
  const handleViewPayslip = async (runId, staffId) => {
    try {
      const data = await payrollService.getPayslip(runId, staffId);
      setPayslipData(data);
      setSelectedPayslip(`${runId}-${staffId}`);
      setShowPayslipModal(true);
    } catch (error) {
      toast.error('Failed to load payslip');
    }
  };

  // Approve payroll run
  const handleApproveRun = (runId) => {
    showDialog({
      title: 'Approve Payroll Run',
      message: 'Are you sure you want to approve this payroll run?',
      type: 'warning',
      primaryAction: {
        label: 'Approve',
        onClick: async () => {
          closeDialog();
          try {
            await payrollService.approveRun(runId);
            toast.success('Payroll run approved successfully!');
            loadPayrollRuns();
          } catch (error) {
            toast.error(error.message || 'Failed to approve payroll run');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  // Reject payroll run (send back to draft for revision)
  const handleRejectRun = (runId) => {
    showDialog({
      title: 'Reject Payroll Run',
      message: 'Reject this payroll run? It will be marked rejected and will not be posted or paid.',
      type: 'warning',
      primaryAction: {
        label: 'Reject',
        onClick: async () => {
          closeDialog();
          try {
            await payrollService.rejectRun(runId);
            toast.success('Payroll run rejected');
            loadPayrollRuns();
          } catch (error) {
            toast.error(error.message || 'Failed to reject payroll run');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  // Download payslip as PDF
  // Downloads the real backend-generated payslip PDF (services/payslip_pdf_service.py),
  // not a client-side print — mirrors ReportCardPreviewModal.js's blob-download pattern.
  const handleDownloadPayslip = async () => {
    if (!payslipData) return;
    try {
      const response = await payrollService.getPayslipPdf(payslipData.payroll_run_id, payslipData.staff_id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip_${payslipData.staff_name}_${payslipData.period_name}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Payslip PDF downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download payslip PDF');
    }
  };

  // Post payroll run
  const handlePostRun = (runId) => {
    showDialog({
      title: 'Post Payroll Run',
      message: 'Are you sure you want to post (finalize) this payroll run? This cannot be undone.',
      type: 'warning',
      primaryAction: {
        label: 'Post',
        onClick: async () => {
          closeDialog();
          try {
            await payrollService.postRun(runId);
            toast.success('Payroll run posted successfully!');
            loadPayrollRuns();
          } catch (error) {
            toast.error(error.message || 'Failed to post payroll run');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  // Pay (disburse) a posted payroll run via Paystack Transfers
  const handlePayRun = (runId) => {
    showDialog({
      title: 'Pay Staff',
      message: 'Send real payments to every verified staff member on this run via Paystack? Staff without verified payout details will be skipped and can be retried later.',
      type: 'warning',
      primaryAction: {
        label: 'Pay Now',
        onClick: async () => {
          closeDialog();
          try {
            setPaying(true);
            const result = await payrollService.payRun(runId);
            if (result.failed_count > 0) {
              toast.warning(`${result.message}. ${result.errors?.slice(0, 3).join('; ') || ''}`);
            } else {
              toast.success(result.message);
            }
            loadPayrollRuns();
          } catch (error) {
            toast.error(error.message || 'Failed to disburse payroll run');
          } finally {
            setPaying(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', icon: Clock, color: 'badge-warning' },
      generated: { label: 'Generated', icon: CheckCircle, color: 'badge-success' },
      approved: { label: 'Approved', icon: CheckCircle, color: 'badge-info' },
      posted: { label: 'Posted', icon: CheckCircle, color: 'badge-success' },
      rejected: { label: 'Rejected', icon: Warning, color: 'badge-error' }
    };
    const config = statusConfig[status] || { label: status, icon: Clock, color: 'badge-default' };
    const StatusIcon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <StatusIcon size={14} weight="bold" /> {config.label}
      </span>
    );
  };

  const getLoanStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', icon: Clock, color: 'badge-warning' },
      approved: { label: 'Approved', icon: CheckCircle, color: 'badge-info' },
      active: { label: 'Active', icon: CheckCircle, color: 'badge-info' },
      completed: { label: 'Completed', icon: CheckCircle, color: 'badge-success' },
      rejected: { label: 'Rejected', icon: Warning, color: 'badge-error' },
      cancelled: { label: 'Cancelled', icon: Warning, color: 'badge-default' }
    };
    const config = statusConfig[status] || { label: status, icon: Clock, color: 'badge-default' };
    const StatusIcon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <StatusIcon size={14} weight="bold" /> {config.label}
      </span>
    );
  };

  const getEncashmentStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', icon: Clock, color: 'badge-warning' },
      approved: { label: 'Approved', icon: CheckCircle, color: 'badge-info' },
      paid: { label: 'Paid', icon: CheckCircle, color: 'badge-success' },
      rejected: { label: 'Rejected', icon: Warning, color: 'badge-error' }
    };
    const config = statusConfig[status] || { label: status, icon: Clock, color: 'badge-default' };
    const StatusIcon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <StatusIcon size={14} weight="bold" /> {config.label}
      </span>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleRunSort = (key) => {
    let direction = 'asc';
    if (runSortConfig.key === key && runSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setRunSortConfig({ key, direction });
  };

  const SortIndicator = ({ column }) => {
    if (sortConfig.key !== column) {
      return <span style={{ marginLeft: '4px', opacity: 0.3 }}>⬍</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    ) : (
      <ArrowDown size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    );
  };

  const RunSortIndicator = ({ column }) => {
    if (runSortConfig.key !== column) {
      return <span style={{ marginLeft: '4px', opacity: 0.3 }}>⬍</span>;
    }
    return runSortConfig.direction === 'asc' ? (
      <ArrowUp size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    ) : (
      <ArrowDown size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    );
  };

  const getSortedContracts = () => {
    const sorted = [...contracts].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const getSortedRuns = () => {
    const sorted = [...payrollRuns].sort((a, b) => {
      let aVal = a[runSortConfig.key];
      let bVal = b[runSortConfig.key];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return runSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return runSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleExportPayroll = async (runId) => {
    try {
      setSubmitting(true);
      const run = payrollRuns.find(r => r.id === runId);
      if (!run) {
        toast.error('Payroll run not found');
        return;
      }

      // Fetch line items for the run
      const lineItemsData = await payrollService.getRunLineItems(runId, 1000, 0);
      const lineItems = lineItemsData.items || [];

      // Create CSV content
      const headers = ['Staff ID', 'Staff Name', 'Basic Salary', 'Allowances', 'Gross Amount', 'Tax', 'Pension', 'NSSF', 'Other Deductions', 'Net Amount'];
      const rows = lineItems.map(item => [
        item.staff_id || '',
        item.staff_name || '',
        item.basic_salary || 0,
        item.total_allowances || 0,
        item.gross_amount || 0,
        item.tax_amount || 0,
        item.pension_amount || 0,
        item.nssf_amount || 0,
        item.other_deductions || 0,
        item.net_amount || 0
      ]);

      // Add summary row
      const summaryRow = [
        'TOTAL',
        '',
        lineItems.reduce((sum, item) => sum + (item.basic_salary || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.total_allowances || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.gross_amount || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.pension_amount || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.nssf_amount || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.other_deductions || 0), 0),
        lineItems.reduce((sum, item) => sum + (item.net_amount || 0), 0)
      ];
      rows.push(summaryRow);

      // Create CSV string
      const csvContent = [
        `Payroll Export - ${run.period_name}`,
        `Generated on: ${new Date().toLocaleDateString()}`,
        '',
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          if (typeof cell === 'string' && cell.includes(',')) {
            return `"${cell}"`;
          }
          return cell;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payroll_${run.period_name.replace(/\s+/g, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Payroll exported successfully!');
      setOpenDropdownId(null);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export payroll');
    } finally {
      setSubmitting(false);
    }
  };

  // Downloads the Ghana SSNIT contribution schedule CSV from the backend
  // (services/statutory_export_service.py) — distinct from handleExportPayroll's
  // generic client-built CSV above.
  const handleDownloadSsnitExport = async (runId) => {
    try {
      const run = payrollRuns.find(r => r.id === runId);
      const response = await payrollService.getSsnitExport(runId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ssnit_${(run?.period_name || runId).replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('SSNIT contribution schedule downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download SSNIT export');
    } finally {
      setOpenDropdownId(null);
    }
  };

  // Downloads the bank payment export CSV for a run (only available once a
  // run is approved or posted, same gate as the SSNIT export above).
  const handleDownloadBankPaymentExport = async (runId) => {
    try {
      const run = payrollRuns.find(r => r.id === runId);
      const response = await payrollService.getBankPaymentExport(runId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bank_payment_${(run?.period_name || runId).replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Bank payment export downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download bank payment export');
    } finally {
      setOpenDropdownId(null);
    }
  };

  // Year-level report, not tied to a single run.
  const handleDownloadAnnualSummary = async () => {
    try {
      const response = await payrollService.getAnnualSummary(annualSummaryYear);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `annual_summary_${annualSummaryYear}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Annual summary downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download annual summary');
    }
  };

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Payroll Management</h1>
            <p>Manage staff contracts and process payroll</p>
          </div>
          <div className="header-actions">
            {view === 'contracts' && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowBulkImportModal(true)}
                  title="Import multiple contracts from CSV"
                >
                  <Download size={18} weight="bold" /> Bulk Import
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowContractModal(true)}
                >
                  <Plus size={18} weight="bold" /> New Contract
                </button>
              </>
            )}
            {view === 'runs' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowGenerateRunModal(true)}
              >
                <Plus size={18} weight="bold" /> Generate Run
              </button>
            )}
            {view === 'loans' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowLoanModal(true)}
              >
                <Plus size={18} weight="bold" /> New Loan/Advance
              </button>
            )}
            {view === 'encashments' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowEncashmentModal(true)}
              >
                <Plus size={18} weight="bold" /> New Leave Encashment
              </button>
            )}
          </div>
        </div>

        {/* Annual Summary — a year-level report, not tied to any single run */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
            Annual Summary for
            <input
              type="number"
              value={annualSummaryYear}
              onChange={(e) => setAnnualSummaryYear(Number(e.target.value))}
              style={{ width: 90, padding: 6, borderRadius: 6, border: '1px solid #d1d5db' }}
            />
          </label>
          <button className="btn btn-sm btn-secondary" onClick={handleDownloadAnnualSummary}>
            <Download size={14} /> Download
          </button>
        </div>

        {/* View Tabs */}
        <div className="view-tabs">
          <button
            className={`tab ${view === 'contracts' ? 'active' : ''}`}
            onClick={() => setView('contracts')}
          >
            Contracts
          </button>
          <button
            className={`tab ${view === 'runs' ? 'active' : ''}`}
            onClick={() => setView('runs')}
          >
            Payroll Runs
          </button>
          <button
            className={`tab ${view === 'loans' ? 'active' : ''}`}
            onClick={() => setView('loans')}
          >
            Loans/Advances
          </button>
          <button
            className={`tab ${view === 'encashments' ? 'active' : ''}`}
            onClick={() => setView('encashments')}
          >
            Leave Encashment
          </button>
        </div>

        {/* Deduction Rules View */}
        {view === 'rules' && <DeductionRulesPage />}

        {/* Contracts View */}
        {view === 'contracts' && (
          <div>
            {expiringContracts.length > 0 && (
              <div className="empty-state" style={{ background: '#fff8e1', border: '1px solid #f5c542', textAlign: 'left', padding: '16px', marginBottom: '16px' }}>
                <p style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, marginBottom: '10px' }}>
                  <Warning size={18} /> {expiringContracts.length} contract{expiringContracts.length !== 1 ? 's' : ''} expired or expiring within 30 days
                </p>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr><th>Staff</th><th>Expiry date</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {expiringContracts.map(item => (
                        <tr key={item.id}>
                          <td>{item.staff_name}</td>
                          <td>{new Date(item.effective_to).toLocaleDateString()}</td>
                          <td>{item.status === 'expired' ? <span className="badge badge-error"><Warning size={14} weight="bold" /> Expired</span> : <span className="capitalize" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={14} /> {item.days_until_expiry} day{item.days_until_expiry !== 1 ? 's' : ''} left</span>}</td>
                          <td><button className="btn btn-sm btn-primary" onClick={() => handleRenewContract(item.id)}>Renew</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {loading ? (
              <div className="loading">Loading contracts...</div>
            ) : contracts.length === 0 ? (
              <div className="empty-state">
                <p>No payroll contracts found</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowContractModal(true)}
                >
                  Create First Contract
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('staff_name')}>
                        Staff <SortIndicator column="staff_name" />
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('basic_salary')}>
                        Basic Salary <SortIndicator column="basic_salary" />
                      </th>
                      <th>Allowances</th>
                      <th>Deductions</th>
                      <th>Schedule</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedContracts().map((contract) => (
                      <tr key={contract.id}>
                        <td>
                          <strong>{contract.staff_name}</strong>
                        </td>
                        <td>{formatCurrency(contract.basic_salary)}</td>
                        <td>{formatCurrency(contract.total_allowances)}</td>
                        <td style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          Tax: {contract.tax_rate_percent}%<br />
                          Pension: {contract.pension_rate_percent}%<br />
                          NSSF: {contract.nssf_rate_percent}%<br />
                          Other: {formatCurrency(contract.other_deduction)}
                        </td>
                        <td className="capitalize">{contract.pay_schedule}</td>
                        <td>
                          {getStatusBadge(
                            contract.status === 'active' ? 'generated' : 'draft'
                          )}
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-ghost"
                            onClick={() => handleViewContract(contract)}
                          >
                            <Eye size={16} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payroll Runs View */}
        {view === 'runs' && (
          <div>
            {loading ? (
              <div className="loading">Loading payroll runs...</div>
            ) : payrollRuns.length === 0 ? (
              <div className="empty-state">
                <p>No payroll runs found</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowGenerateRunModal(true)}
                >
                  Generate First Payroll
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleRunSort('period_name')}>
                        Period <RunSortIndicator column="period_name" />
                      </th>
                      <th>Schedule</th>
                      <th>Status</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleRunSort('staff_count')}>
                        Staff Count <RunSortIndicator column="staff_count" />
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleRunSort('total_gross')}>
                        Total Gross <RunSortIndicator column="total_gross" />
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleRunSort('total_net')}>
                        Total Net <RunSortIndicator column="total_net" />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedRuns().map((run) => (
                      <tr key={run.id}>
                        <td>
                          <strong>{run.period_name}</strong>
                        </td>
                        <td className="capitalize">{run.pay_schedule || 'monthly'}</td>
                        <td>{getStatusBadge(run.status)}</td>
                        <td>{run.staff_count} staff</td>
                        <td>{formatCurrency(run.total_gross)}</td>
                        <td>{formatCurrency(run.total_net)}</td>
                        <td className="actions-column">
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => handleViewLineItems(run.id)}
                            title="View line items"
                          >
                            <Eye size={16} />
                          </button>
                          {run.status === 'generated' && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApproveRun(run.id)}
                                title="Approve run"
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRejectRun(run.id)}
                                title="Reject run"
                              >
                                <XCircle size={16} />
                              </button>
                            </>
                          )}
                          {run.status === 'approved' && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handlePostRun(run.id)}
                                title="Post run"
                              >
                                <CheckCircle size={16} />
                              </button>
                            </>
                          )}
                          {run.status === 'posted' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePayRun(run.id)}
                              disabled={paying}
                              title="Pay staff via Paystack"
                            >
                              <Money size={16} /> {paying ? 'Paying...' : 'Pay'}
                            </button>
                          )}
                          <div style={{ position: 'relative' }}>
                            <button 
                              className="btn btn-sm btn-ghost"
                              onClick={() => setOpenDropdownId(openDropdownId === run.id ? null : run.id)}
                              title="More actions"
                            >
                              <CaretDown size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Dropdown Menu Portal */}
        {openDropdownId && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            onClick: () => setOpenDropdownId(null)
          }}>
            <div style={{
              position: 'absolute',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              minWidth: '200px',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => {
                  const run = payrollRuns.find(r => r.id === openDropdownId);
                  if (run) handleViewLineItems(run.id);
                  setOpenDropdownId(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  borderBottom: '1px solid #eee',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <ClipboardText size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> View Details
              </button>
              {payrollRuns.find(r => r.id === openDropdownId)?.status === 'posted' && (
                <button
                  onClick={() => {
                    const run = payrollRuns.find(r => r.id === openDropdownId);
                    if (run) {
                      handleExportPayroll(run.id);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    borderBottom: '1px solid #eee',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                  disabled={submitting}
                >
                  <FloppyDisk size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Export
                </button>
              )}
              {['approved', 'posted'].includes(payrollRuns.find(r => r.id === openDropdownId)?.status) && (
                <button
                  onClick={() => handleDownloadSsnitExport(openDropdownId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    borderBottom: '1px solid #eee',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <Money size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> SSNIT Export
                </button>
              )}
              {['approved', 'posted'].includes(payrollRuns.find(r => r.id === openDropdownId)?.status) && (
                <button
                  onClick={() => handleDownloadBankPaymentExport(openDropdownId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    borderBottom: '1px solid #eee',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <Money size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Bank Payment Export
                </button>
              )}
              <button
                onClick={() => {
                  const run = payrollRuns.find(r => r.id === openDropdownId);
                  if (run) {
                    showDialog({
                      title: 'Payroll Run Details',
                      message: `Period: ${run.period_name}\nStatus: ${run.status}\nGross Salary: ${formatCurrency(run.total_gross)}\nNet Salary: ${formatCurrency(run.total_net)}`,
                      type: 'info',
                      primaryAction: {
                        label: 'Close',
                        onClick: closeDialog
                      }
                    });
                  }
                  setOpenDropdownId(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <ChartBar size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> GL Posting
              </button>
            </div>
          </div>
        )}

        {/* Staff Loans / Advances View */}
        {view === 'loans' && (
          <div>
            {loading ? (
              <div className="loading">Loading loans...</div>
            ) : loans.length === 0 ? (
              <div className="empty-state">
                <p>No staff loans or advances found</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowLoanModal(true)}
                >
                  New Loan/Advance
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Type</th>
                      <th>Principal</th>
                      <th>Installment</th>
                      <th>Paid</th>
                      <th>Outstanding Balance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.map((loan) => (
                      <tr key={loan.id}>
                        <td>{loan.staff_name}</td>
                        <td className="capitalize">{loan.loan_type}</td>
                        <td>{formatCurrency(loan.principal_amount)}</td>
                        <td>{formatCurrency(loan.installment_amount)}</td>
                        <td>{loan.installments_paid} / {loan.total_installments}</td>
                        <td>{formatCurrency(loan.outstanding_balance)}</td>
                        <td>{getLoanStatusBadge(loan.status)}</td>
                        <td className="actions-column">
                          {loan.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApproveLoan(loan.id)}
                                title="Approve and disburse"
                              >
                                <CheckCircle size={16} /> Approve
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRejectLoan(loan.id)}
                                title="Reject"
                              >
                                <XCircle size={16} /> Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Leave Encashment View */}
        {view === 'encashments' && (
          <div>
            {loading ? (
              <div className="loading">Loading leave encashment requests...</div>
            ) : leaveEncashments.length === 0 ? (
              <div className="empty-state">
                <p>No leave encashment requests found</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowEncashmentModal(true)}
                >
                  New Leave Encashment
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Leave Days</th>
                      <th>Daily Rate</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveEncashments.map((req) => (
                      <tr key={req.id}>
                        <td>{req.staff_name}</td>
                        <td>{req.leave_days}</td>
                        <td>{formatCurrency(req.daily_rate)}</td>
                        <td>{formatCurrency(req.encashment_amount)}</td>
                        <td>{getEncashmentStatusBadge(req.status)}</td>
                        <td className="actions-column">
                          {req.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApproveEncashment(req.id)}
                                title="Approve"
                              >
                                <CheckCircle size={16} /> Approve
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRejectEncashment(req.id)}
                                title="Reject"
                              >
                                <XCircle size={16} /> Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Create Contract Modal */}
        {showContractModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>Create Payroll Contract</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowContractModal(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateContract} className="form">
                <div className="form-group">
                  <label>Staff Member *</label>
                  <select
                    name="staff_id"
                    value={contractForm.staff_id}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        staff_id: e.target.value
                      })
                    }
                    required
                    disabled={submitting}
                  >
                    <option value="">Select staff...</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.full_name || `${staff.first_name} ${staff.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Basic Salary (GHS) *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="basic_salary"
                    value={contractForm.basic_salary}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        basic_salary: e.target.value
                      })
                    }
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Pay Schedule</label>
                  <select
                    name="pay_schedule"
                    value={contractForm.pay_schedule}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        pay_schedule: e.target.value
                      })
                    }
                    disabled={submitting}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>

                <fieldset className="form-section" disabled={submitting}>
                  <legend>Allowances (Optional)</legend>
                  <div className="form-group">
                    <label>Housing Allowance (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="allowance_housing"
                      value={contractForm.allowance_housing}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_housing: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Transport Allowance (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="allowance_transport"
                      value={contractForm.allowance_transport}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_transport: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Meals Allowance (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="allowance_meals"
                      value={contractForm.allowance_meals}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_meals: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Utilities Allowance (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="allowance_utilities"
                      value={contractForm.allowance_utilities}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_utilities: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Other Allowance (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="allowance_other"
                      value={contractForm.allowance_other}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_other: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Other Allowance Description</label>
                    <input
                      type="text"
                      name="allowance_other_description"
                      value={contractForm.allowance_other_description}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          allowance_other_description: e.target.value
                        })
                      }
                      placeholder="e.g., Special duty allowance"
                    />
                  </div>
                </fieldset>

                <fieldset className="form-section" disabled={submitting}>
                  <legend>Deductions (Optional)</legend>
                  <div className="form-group">
                    <label>Income Tax Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      name="tax_rate_percent"
                      value={contractForm.tax_rate_percent}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          tax_rate_percent: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Pension Contribution Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      name="pension_rate_percent"
                      value={contractForm.pension_rate_percent}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          pension_rate_percent: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>NSSF Contribution Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      name="nssf_rate_percent"
                      value={contractForm.nssf_rate_percent}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          nssf_rate_percent: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Other Deduction (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="other_deduction"
                      value={contractForm.other_deduction}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          other_deduction: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Other Deduction Description</label>
                    <input
                      type="text"
                      name="other_deduction_description"
                      value={contractForm.other_deduction_description}
                      onChange={(e) =>
                        setContractForm({
                          ...contractForm,
                          other_deduction_description: e.target.value
                        })
                      }
                      placeholder="e.g., Loan repayment"
                    />
                  </div>
                </fieldset>

                <div className="form-group">
                  <label>Effective From *</label>
                  <input
                    type="date"
                    name="effective_from"
                    value={contractForm.effective_from}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        effective_from: e.target.value
                      })
                    }
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={contractForm.notes}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        notes: e.target.value
                      })
                    }
                    placeholder="Add any notes about this contract..."
                    disabled={submitting}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowContractModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : 'Create Contract'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Generate Payroll Run Modal */}
        {showGenerateRunModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Generate Payroll Run</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowGenerateRunModal(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleGenerateRun} className="form">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    min="2020"
                    max="2030"
                    name="period_year"
                    value={runForm.period_year}
                    onChange={(e) =>
                      setRunForm({
                        ...runForm,
                        period_year: parseInt(e.target.value)
                      })
                    }
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Month *</label>
                  <select
                    name="period_month"
                    value={runForm.period_month}
                    onChange={(e) =>
                      setRunForm({
                        ...runForm,
                        period_month: parseInt(e.target.value)
                      })
                    }
                    required
                    disabled={submitting}
                  >
                    {[
                      'January',
                      'February',
                      'March',
                      'April',
                      'May',
                      'June',
                      'July',
                      'August',
                      'September',
                      'October',
                      'November',
                      'December'
                    ].map((month, idx) => (
                      <option key={idx} value={idx + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Pay Schedule *</label>
                  <select
                    name="pay_schedule"
                    value={runForm.pay_schedule}
                    onChange={(e) =>
                      setRunForm({
                        ...runForm,
                        pay_schedule: e.target.value
                      })
                    }
                    disabled={submitting}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                    Only staff whose contract uses this schedule are included — a school can run monthly payroll for most staff and a separate weekly run for others in the same month.
                  </p>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={runForm.notes}
                    onChange={(e) =>
                      setRunForm({
                        ...runForm,
                        notes: e.target.value
                      })
                    }
                    placeholder="Add any notes about this payroll run..."
                    disabled={submitting}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowGenerateRunModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Generating...
                      </span>
                    ) : 'Generate Payroll Run'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Line Items Modal */}
        {showLineItemsModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '1100px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>Payroll Line Items</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowLineItemsModal(false)}
                >
                  <X size={24} />
                </button>
              </div>

              {runLineItems.length === 0 ? (
                <p>No line items found</p>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Staff</th>
                        <th>Basic</th>
                        <th>Allowances</th>
                        <th>Gross</th>
                        <th>Deductions</th>
                        <th>Adjustments</th>
                        <th>Net</th>
                        <th>Payment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runLineItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.staff_name}</td>
                          <td>{formatCurrency(item.basic_salary)}</td>
                          <td>{formatCurrency(item.total_allowances)}</td>
                          <td>{formatCurrency(item.gross_amount)}</td>
                          <td>{formatCurrency(item.total_deductions)}</td>
                          <td>{formatCurrency(item.total_adjustments || 0)}</td>
                          <td className="font-bold">{formatCurrency(item.net_amount)}</td>
                          <td>
                            {item.payment_status === 'paid' ? (
                              <span className="badge badge-success"><CheckCircle size={14} weight="bold" /> Paid</span>
                            ) : item.payment_status === 'failed' ? (
                              <span className="badge badge-error" title={item.payment_failure_reason}><Warning size={14} weight="bold" /> Failed</span>
                            ) : (
                              <span className="badge badge-default">Unpaid</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() =>
                                handleViewPayslip(selectedRun, item.staff_id)
                              }
                            >
                              <Eye size={16} /> Payslip
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Adjustments */}
              <div style={{ marginTop: 24, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                <h3 style={{ fontSize: 16, marginBottom: 8 }}>Adjustments (bonus / penalty)</h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  Only affects net pay once approved. Available while this run is still generated (not yet approved/posted).
                </p>

                <form onSubmit={handleCreateAdjustment} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1.6fr auto', gap: 8, marginBottom: 16, alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 12 }}>Staff</label>
                    <select
                      value={adjustmentForm.staff_id}
                      onChange={(e) => setAdjustmentForm({ ...adjustmentForm, staff_id: e.target.value })}
                      disabled={submittingAdjustment}
                    >
                      <option value="">Select staff...</option>
                      {runLineItems.map((item) => (
                        <option key={item.staff_id} value={item.staff_id}>{item.staff_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 12 }}>Type</label>
                    <select
                      value={adjustmentForm.adjustment_type}
                      onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_type: e.target.value })}
                      disabled={submittingAdjustment}
                    >
                      <option value="bonus">Bonus</option>
                      <option value="refund">Refund</option>
                      <option value="penalty">Penalty</option>
                      <option value="advance_recovery">Advance recovery</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 12 }}>Amount (GHS)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={adjustmentForm.amount}
                      onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                      disabled={submittingAdjustment}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 12 }}>Reason</label>
                    <input
                      type="text"
                      value={adjustmentForm.reason}
                      onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                      placeholder="e.g., Performance bonus"
                      disabled={submittingAdjustment}
                    />
                  </div>
                  <button type="submit" className="btn btn-sm btn-primary" disabled={submittingAdjustment}>
                    <Plus size={14} /> Add
                  </button>
                </form>

                {adjustments.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>No adjustments on this run.</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Staff</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Reason</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adjustments.map((adj) => (
                          <tr key={adj.id}>
                            <td>{adj.staff_name}</td>
                            <td className="capitalize">{adj.adjustment_type.replace('_', ' ')}</td>
                            <td>{formatCurrency(adj.amount)}</td>
                            <td style={{ fontSize: 13 }}>{adj.reason}</td>
                            <td>
                              {adj.status === 'approved' ? (
                                <span className="badge badge-success"><CheckCircle size={14} weight="bold" /> Approved</span>
                              ) : (
                                <span className="badge badge-warning"><Clock size={14} weight="bold" /> Pending</span>
                              )}
                            </td>
                            <td>
                              {adj.status !== 'approved' && (
                                <>
                                  <button className="btn btn-sm btn-success" onClick={() => handleApproveAdjustment(adj.id)} title="Approve">
                                    <CheckCircle size={14} />
                                  </button>
                                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAdjustment(adj.id)} title="Delete">
                                    <Trash size={14} />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contract Details Modal */}
        {showContractDetailsModal && selectedContract && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h2>Contract Details</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowContractDetailsModal(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Staff Name</label>
                  <p style={{ margin: '8px 0 0 0' }}>{selectedContract.staff_name}</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Basic Salary</label>
                    <p style={{ margin: '8px 0 0 0' }}>{formatCurrency(selectedContract.basic_salary)}</p>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Pay Schedule</label>
                    <p style={{ margin: '8px 0 0 0', textTransform: 'capitalize' }}>{selectedContract.pay_schedule}</p>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Total Allowances</label>
                  <p style={{ margin: '8px 0 0 0' }}>{formatCurrency(selectedContract.total_allowances)}</p>
                </div>

                {selectedContract.total_allowances > 0 && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 600, margin: '0 0 8px 0' }}>Allowance Breakdown:</p>
                    {selectedContract.allowance_housing > 0 && (
                      <div style={{ fontSize: '13px', margin: '4px 0' }}>Housing: {formatCurrency(selectedContract.allowance_housing)}</div>
                    )}
                    {selectedContract.allowance_transport > 0 && (
                      <div style={{ fontSize: '13px', margin: '4px 0' }}>Transport: {formatCurrency(selectedContract.allowance_transport)}</div>
                    )}
                    {selectedContract.allowance_meals > 0 && (
                      <div style={{ fontSize: '13px', margin: '4px 0' }}>Meals: {formatCurrency(selectedContract.allowance_meals)}</div>
                    )}
                    {selectedContract.allowance_utilities > 0 && (
                      <div style={{ fontSize: '13px', margin: '4px 0' }}>Utilities: {formatCurrency(selectedContract.allowance_utilities)}</div>
                    )}
                    {selectedContract.allowance_other > 0 && (
                      <div style={{ fontSize: '13px', margin: '4px 0' }}>Other: {formatCurrency(selectedContract.allowance_other)}</div>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Tax Rate</label>
                    <p style={{ margin: '8px 0 0 0' }}>{selectedContract.tax_rate_percent}%</p>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Pension Rate</label>
                    <p style={{ margin: '8px 0 0 0' }}>{selectedContract.pension_rate_percent}%</p>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>NSSF Rate</label>
                    <p style={{ margin: '8px 0 0 0' }}>{selectedContract.nssf_rate_percent}%</p>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Other Deduction</label>
                    <p style={{ margin: '8px 0 0 0' }}>{formatCurrency(selectedContract.other_deduction)}</p>
                  </div>
                </div>

                {selectedContract.notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Notes</label>
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>{selectedContract.notes}</p>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowContractDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payslip Modal */}
        {showPayslipModal && payslipData && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h2>Payslip - {payslipData.period_name}</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowPayslipModal(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="payslip">
                <div className="payslip-header">
                  <p className="text-muted">{payslipData.school_name || 'School'}</p>
                  <h3>{payslipData.staff_name}</h3>
                  <p className="text-muted">{payslipData.period_name}</p>
                </div>

                <div className="payslip-section">
                  <h4>Earnings</h4>
                  <div className="payslip-line">
                    <span>Basic Salary</span>
                    <span>{formatCurrency(payslipData.basic_salary)}</span>
                  </div>
                  {payslipData.total_allowances > 0 && (
                    <div className="payslip-line">
                      <span>Allowances</span>
                      <span>{formatCurrency(payslipData.total_allowances)}</span>
                    </div>
                  )}
                  <div className="payslip-line font-bold border-top border-bottom">
                    <span>Gross Amount</span>
                    <span>{formatCurrency(payslipData.gross_amount)}</span>
                  </div>
                </div>

                <div className="payslip-section">
                  <h4>Deductions</h4>
                  {payslipData.tax_amount > 0 && (
                    <div className="payslip-line">
                      <span>Income Tax</span>
                      <span>-{formatCurrency(payslipData.tax_amount)}</span>
                    </div>
                  )}
                  {payslipData.pension_amount > 0 && (
                    <div className="payslip-line">
                      <span>Pension</span>
                      <span>-{formatCurrency(payslipData.pension_amount)}</span>
                    </div>
                  )}
                  {payslipData.nssf_amount > 0 && (
                    <div className="payslip-line">
                      <span>NSSF</span>
                      <span>-{formatCurrency(payslipData.nssf_amount)}</span>
                    </div>
                  )}
                  {payslipData.other_deductions > 0 && (
                    <div className="payslip-line">
                      <span>Other Deductions</span>
                      <span>-{formatCurrency(payslipData.other_deductions)}</span>
                    </div>
                  )}
                  <div className="payslip-line font-bold border-top border-bottom">
                    <span>Total Deductions</span>
                    <span>-{formatCurrency(payslipData.total_deductions)}</span>
                  </div>
                </div>

                <div className="payslip-section">
                  <div className="payslip-line font-bold text-lg">
                    <span>Net Amount</span>
                    <span>{formatCurrency(payslipData.net_amount)}</span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDownloadPayslip}
                >
                  <FilePdf size={16} /> Download PDF
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPayslipModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Loan/Advance Modal */}
        {showLoanModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>New Loan/Advance</h2>
                <button className="close-btn" onClick={() => setShowLoanModal(false)}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateLoan} className="form">
                <div className="form-group">
                  <label>Staff Member *</label>
                  <select
                    value={loanForm.staff_id}
                    onChange={(e) => setLoanForm({ ...loanForm, staff_id: e.target.value })}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select staff...</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.full_name || `${staff.first_name} ${staff.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={loanForm.loan_type}
                    onChange={(e) => setLoanForm({ ...loanForm, loan_type: e.target.value })}
                    disabled={submitting}
                  >
                    <option value="loan">Loan</option>
                    <option value="advance">Advance</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Principal Amount (GHS) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={loanForm.principal_amount}
                    onChange={(e) => setLoanForm({ ...loanForm, principal_amount: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Installment Amount (GHS) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={loanForm.installment_amount}
                    onChange={(e) => setLoanForm({ ...loanForm, installment_amount: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Total Installments *</label>
                  <input
                    type="number"
                    min="1"
                    value={loanForm.total_installments}
                    onChange={(e) => setLoanForm({ ...loanForm, total_installments: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Start Period Year *</label>
                  <input
                    type="number"
                    value={loanForm.start_period_year}
                    onChange={(e) => setLoanForm({ ...loanForm, start_period_year: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Start Period Month *</label>
                  <select
                    value={loanForm.start_period_month}
                    onChange={(e) => setLoanForm({ ...loanForm, start_period_month: e.target.value })}
                    disabled={submitting}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Reason</label>
                  <input
                    type="text"
                    value={loanForm.reason}
                    onChange={(e) => setLoanForm({ ...loanForm, reason: e.target.value })}
                    placeholder="e.g., Emergency medical expense"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? <CircleNotch size={16} className="spin" /> : 'Create Loan Request'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowLoanModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* New Leave Encashment Modal */}
        {showEncashmentModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>New Leave Encashment</h2>
                <button className="close-btn" onClick={() => setShowEncashmentModal(false)}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateEncashment} className="form">
                <div className="form-group">
                  <label>Staff Member *</label>
                  <select
                    value={encashmentForm.staff_id}
                    onChange={(e) => setEncashmentForm({ ...encashmentForm, staff_id: e.target.value })}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select staff...</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.full_name || `${staff.first_name} ${staff.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Unused Leave Days *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={encashmentForm.leave_days}
                    onChange={(e) => setEncashmentForm({ ...encashmentForm, leave_days: e.target.value })}
                    required
                    disabled={submitting}
                  />
                  <small className="text-muted">
                    Daily rate is computed from the staff member's basic salary ÷ 30 — there's
                    no leave-balance tracking in this system, so enter the days manually.
                  </small>
                </div>

                <div className="form-group">
                  <label>Reason</label>
                  <input
                    type="text"
                    value={encashmentForm.reason}
                    onChange={(e) => setEncashmentForm({ ...encashmentForm, reason: e.target.value })}
                    placeholder="e.g., Unused annual leave for 2026"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? <CircleNotch size={16} className="spin" /> : 'Create Request'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEncashmentModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Dialog Modal */}
      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
        secondaryAction={dialogConfig.secondaryAction}
      />

      {/* Bulk Import Modal */}
      <ContractBulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          loadContracts();
        }}
      />
    </Layout>
  );
}

export default PayrollPage;
